import React, { useEffect, useState } from 'react';
import { PlusIcon, LinkIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { ProfessionalExpense } from '../types';
import { AddExpenseModal } from './AddExpenseModal';

interface LinkedExpensesSectionProps {
  selectedIds: string[];
  onChange: (ids: string[], expenses: ProfessionalExpense[]) => void;
}

export const LinkedExpensesSection: React.FC<LinkedExpensesSectionProps> = ({
  selectedIds,
  onChange
}) => {
  const [expenses, setExpenses] = useState<ProfessionalExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInlineAdd, setShowInlineAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await apiService.getUnlinkedExpenses();
    setExpenses(res.data?.expenses || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (exp: ProfessionalExpense) => {
    const next = selectedIds.includes(exp.id)
      ? selectedIds.filter((id) => id !== exp.id)
      : [...selectedIds, exp.id];
    const selected = expenses.filter((e) => next.includes(e.id));
    onChange(next, selected);
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <LinkIcon className="h-4 w-4 text-blue-600" />
          Gastos vinculados
        </h3>
        <button
          type="button"
          onClick={() => setShowInlineAdd(true)}
          className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Añadir nuevo
        </button>
      </div>

      <p className="text-xs text-gray-600">
        Seleccione gastos no suplido (repercutibles) o suplidos sin vincular. Se añadirán como líneas en la factura.
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">Cargando gastos…</p>
      ) : expenses.length === 0 ? (
        <p className="text-sm text-gray-500">No hay gastos disponibles para vincular.</p>
      ) : (
        <ul className="max-h-40 overflow-y-auto space-y-2">
          {expenses.map((exp) => (
            <li key={exp.id}>
              <label className="flex items-center gap-3 p-2 rounded-md bg-white border cursor-pointer hover:border-blue-300">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(exp.id)}
                  onChange={() => toggle(exp)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="flex-1 text-sm">
                  <span className="font-medium">{exp.proveedor}</span>
                  <span className="text-gray-500 ml-2">{exp.fecha}</span>
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100">
                  {exp.expenseKind === 'suplido' ? 'Suplido' : 'Repercutible'}
                </span>
                <span className="text-sm font-medium">{fmt(exp.total)}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {selectedIds.length > 0 && (
        <p className="text-xs text-blue-700">
          {selectedIds.length} gasto(s) seleccionado(s) — se vincularán al guardar la factura.
        </p>
      )}

      {showInlineAdd && (
        <AddExpenseModal
          inline
          initialKind="no_suplido"
          onClose={() => setShowInlineAdd(false)}
          onSaved={async (exp) => {
            setShowInlineAdd(false);
            await load();
            if (exp.expenseKind === 'suplido' || exp.expenseKind === 'no_suplido') {
              const next = [...selectedIds, exp.id];
              onChange(next, [...expenses.filter((e) => next.includes(e.id)), exp]);
            }
          }}
        />
      )}
    </div>
  );
};
